import React, { useEffect, useRef, useState } from 'react';
import {
  CameraIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { conferenciaApi } from '../../api/conferencia';
import { conferenciaPendenciaApi } from '../../api/conferenciaPendencia';
import { useFotosConferencia, useEnviarFoto, useRemoverFoto } from '../../hooks/useConferencias';
import type { ConferenciaNfeFoto } from '../../types/conferencia';
import { useToast } from '../ui/Toast';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import { btnPrimario, btnSecundario } from '../ui/iris';

interface GaleriaFotosConferenciaProps {
  conferenciaId: number;
  /** Conferência concluída (ou fonte read-only) — esconde upload e o botão de remover por foto. */
  somenteLeitura?: boolean;
  /**
   * Fonte das fotos: `'nfe'` (endpoints `/conferencia-nfe/...`, padrão — usado pelo workspace de
   * operação) ou `'pendencia'` (endpoints `/conferencia-pendencia/...`, usado pelo dialog de
   * detalhe da pendência — supervisor só-`conferencia_pendencia` não tem a policy
   * `conferencia_nfe` e tomaria 403 chamando a rota direta).
   */
  api?: 'nfe' | 'pendencia';
}

type EstadoImagem = { status: 'carregando' } | { status: 'pronta'; url: string } | { status: 'erro' };

/** Extrai a mensagem de erro que o backend devolve no BadRequest (ver AdicionarFotoAsync
    em ConferenciaNfeService.cs — tipo/tamanho inválido vira 400 `{ message }`). Mesmo idiom
    de DialogoGerarCotacao/MarcaGestaoGrid. */
function mensagemDeErro(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string } } };
  return ax.response?.data?.message ?? fallback;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Encapsula listar/baixar/enviar/remover selecionando o client pelo prop `api` — pra
 * GaleriaFotosConferencia não duplicar a lógica de fetch/mutation dos dois controllers.
 * `removerFoto` não é parametrizado: `ConferenciaPendenciaController` não expõe DELETE de foto
 * (união é leitura+colaboração; remover é ação do dono do fluxo `conferencia_nfe`) — por isso
 * `podeRemover` é `false` pra `api='pendencia'` e o botão de remover não aparece nesse modo,
 * independente de `somenteLeitura`.
 */
function useGaleriaFotos(conferenciaId: number, api: 'nfe' | 'pendencia') {
  const client = api === 'pendencia' ? conferenciaPendenciaApi : conferenciaApi;
  const fotosQuery = useFotosConferencia(conferenciaId, api);
  const enviarFotoMut = useEnviarFoto(api);
  const removerFotoMut = useRemoverFoto();
  return { client, fotosQuery, enviarFotoMut, removerFotoMut, podeRemover: api === 'nfe' };
}

/**
 * Galeria de fotos da conferência (câmera/upload) — miolo extraído do `FotosConferenciaDialog`
 * (Task 8) pra ser reusado pelo dialog de detalhe da pendência (Task 9). GET /anexo(s) devolve só
 * metadados — os bytes de cada foto exigem o Bearer do axios (ver conferenciaApi.baixarFoto /
 * conferenciaPendenciaApi.baixarFoto), então cada miniatura busca o próprio blob e monta um
 * object URL local.
 *
 * Dono do ciclo de vida dessas URLs: cria ao chegar o blob, revoga quando a foto some da lista
 * (outra sessão/aba removeu ou os dados recarregaram) e ao desmontar. Antes esse miolo vivia
 * dentro do `FotosConferenciaDialog`, que ficava PERMANENTEMENTE montado no workspace (o botão
 * "Fotos" só alternava `isOpen`, nunca desmontava o dialog) — por isso havia um 3º ponto de
 * revogação separado, disparado quando `isOpen` virava `false` com o componente ainda vivo. Este
 * componente não recebe `isOpen`: quem o encaixa (o wrapper) só o renderiza enquanto deve estar
 * visível, então "fechar"/"trocar de conferência" e "desmontar" viram o MESMO evento React — o
 * efeito de desmonte abaixo cobre os dois, e reabrir remonta do zero (refetch limpo). Sem isso é
 * vazamento real num dispositivo que fica ligado o turno inteiro na doca.
 */
const GaleriaFotosConferencia: React.FC<GaleriaFotosConferenciaProps> = ({
  conferenciaId,
  somenteLeitura = false,
  api = 'nfe',
}) => {
  const { client, fotosQuery, enviarFotoMut, removerFotoMut, podeRemover } = useGaleriaFotos(conferenciaId, api);
  const { data: fotos, isLoading, error, refetch } = fotosQuery;
  const toast = useToast();
  const confirm = useConfirmDialog();

  const [imagens, setImagens] = useState<Record<number, EstadoImagem>>({});
  const objectUrlsRef = useRef<Map<number, string>>(new Map());
  const emVooRef = useRef<Set<number>>(new Set());
  // true enquanto a instância está montada; o corpo do efeito de desmonte (Ponto 2) o
  // reafirma `true` a cada montagem e o cleanup o vira `false` — o download em voo do Ponto 1
  // consulta isto pra saber se ainda pode gravar. ⚠ Reafirmar no CORPO é obrigatório: em dev o
  // StrictMode faz mount → cleanup (→ false) → mount de novo; sem redefinir `true` no corpo, o
  // ref ficaria preso em `false` e TODO download subsequente seria revogado sem renderizar (foto
  // eternamente "carregando"). Padrão canônico do "isMounted ref".
  const montadoRef = useRef(true);

  // Foto ampliada (lightbox) — a miniatura é minúscula pra um screenshot denso da doca; abrir em
  // tela cheia mostra o blob em resolução original (o backend guarda o arquivo cru), = nítido.
  const [ampliada, setAmpliada] = useState<{ url: string; nome: string | null } | null>(null);

  // Ponto 1 de revogação: busca o blob de cada foto nova da lista e monta o object URL. Fotos
  // que saíram da lista (removidas por outra sessão/aba) têm a URL revogada e o estado limpo
  // aqui mesmo.
  useEffect(() => {
    if (!fotos) return;
    const idsAtuais = new Set(fotos.map((f) => f.id));

    setImagens((prev) => {
      let mudou = false;
      const proximo = { ...prev };
      for (const chave of Object.keys(proximo)) {
        const id = Number(chave);
        if (!idsAtuais.has(id)) {
          delete proximo[id];
          mudou = true;
        }
      }
      return mudou ? proximo : prev;
    });

    objectUrlsRef.current.forEach((url, id) => {
      if (!idsAtuais.has(id)) {
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(id);
      }
    });

    fotos.forEach((foto) => {
      if (objectUrlsRef.current.has(foto.id) || emVooRef.current.has(foto.id)) return;
      emVooRef.current.add(foto.id);
      setImagens((prev) => ({ ...prev, [foto.id]: { status: 'carregando' } }));

      client
        .baixarFoto(foto.id)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          if (!montadoRef.current) {
            // Resposta chegou depois do unmount (usuário fechou o dialog antes do download
            // terminar): esta instância já foi descartada — o `objectUrlsRef` é de um
            // `useRef` novo a cada montagem, então nenhum efeito futuro vai iterar este Map
            // pra revogar. Sem este guard a URL do blob vazaria até o reload da página.
            URL.revokeObjectURL(url);
            return;
          }
          objectUrlsRef.current.set(foto.id, url);
          setImagens((prev) => ({ ...prev, [foto.id]: { status: 'pronta', url } }));
        })
        .catch(() => {
          setImagens((prev) => ({ ...prev, [foto.id]: { status: 'erro' } }));
        })
        .finally(() => {
          emVooRef.current.delete(foto.id);
        });
    });
  }, [fotos, client]);

  // Ponto 2 de revogação: desmonte de verdade — cobre "fechou o dialog" (o wrapper para de
  // renderizar esta galeria) e "navegação saiu do meio", que agora são o mesmo evento React (ver
  // comentário do componente). Só revoga `objectUrlsRef` (mesmo idiom do FotosConferenciaDialog
  // original) — `emVooRef` não precisa de limpeza aqui: os refs são recriados do zero a cada nova
  // montagem, então não há estado obsoleto de "em voo" pra vazar entre uma abertura e a próxima.
  useEffect(() => {
    montadoRef.current = true; // reafirma na (re)montagem — ver comentário do montadoRef (StrictMode)
    return () => {
      montadoRef.current = false;
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    };
  }, []);

  // ESC fecha o lightbox. Em CAPTURE + stopPropagation de propósito: esta galeria vive DENTRO de
  // dialogs que escutam ESC no window (ex.: ConferenciaPendenciaDetalheDialog) — sem parar a
  // propagação, um ESC fecharia o lightbox E o dialog pai junto. Capture roda antes do listener
  // bubble do pai, então o ESC morre aqui quando há foto ampliada.
  useEffect(() => {
    if (!ampliada) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      setAmpliada(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [ampliada]);

  const handleArquivoSelecionado = (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = ''; // permite reselecionar o mesmo arquivo em seguida
    if (!arquivo) return;

    enviarFotoMut.mutate(
      { conferenciaId, arquivo },
      {
        onSuccess: () => toast.sucesso('Foto adicionada.'),
        onError: (err) => toast.erro(mensagemDeErro(err, 'Falha ao enviar a foto.')),
      }
    );
  };

  const handleRemover = async (foto: ConferenciaNfeFoto) => {
    const ok = await confirm({
      titulo: 'Remover foto',
      mensagem: `Remover ${foto.nomeArquivo ? `"${foto.nomeArquivo}"` : 'esta foto'}? Essa ação não pode ser desfeita.`,
      textoConfirmar: 'Remover',
      destrutivo: true,
    });
    if (!ok) return;

    removerFotoMut.mutate(
      { anexoId: foto.id, conferenciaId },
      {
        onSuccess: () => toast.sucesso('Foto removida.'),
        onError: (err) => toast.erro(mensagemDeErro(err, 'Falha ao remover a foto.')),
      }
    );
  };

  const totalFotos = fotos?.length ?? 0;
  const mostrarUpload = !somenteLeitura;
  const mostrarRemover = !somenteLeitura && podeRemover;

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-5">
      {isLoading && (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-tx-3">
          <ArrowPathIcon className="h-5 w-5 animate-spin" /> Carregando fotos…
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-iris border border-danger-border bg-danger-soft p-3 text-sm text-danger">
          <p>Não foi possível carregar as fotos desta conferência.</p>
          <button type="button" onClick={() => refetch()} className={`${btnSecundario} mt-2`}>
            Tentar novamente
          </button>
        </div>
      )}

      {!isLoading && !error && totalFotos === 0 && (
        <div className="rounded-iris border border-line bg-surface-2 px-6 py-10 text-center">
          <PhotoIcon className="mx-auto h-9 w-9 text-tx-3" />
          <p className="mt-3 text-sm font-medium text-tx">Nenhuma foto anexada</p>
          <p className="mt-1 text-[13px] text-tx-3">
            {somenteLeitura
              ? 'Esta conferência foi concluída sem fotos.'
              : 'Use "Tirar foto" para fotografar na hora, ou "Enviar arquivo" para anexar uma imagem da nota, avaria etc.'}
          </p>
        </div>
      )}

      {!isLoading && !error && totalFotos > 0 && (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {fotos!.map((foto) => {
            const estado = imagens[foto.id];
            return (
              <div
                key={foto.id}
                className="relative aspect-square overflow-hidden rounded-iris border border-line bg-surface-2"
              >
                {estado?.status === 'pronta' ? (
                  <button
                    type="button"
                    onClick={() => setAmpliada({ url: estado.url, nome: foto.nomeArquivo })}
                    aria-label={`Ampliar ${foto.nomeArquivo ?? 'foto'}`}
                    title={`${foto.nomeArquivo ?? 'Foto'} · ${formatBytes(foto.tamanhoBytes)} · clique para ampliar`}
                    className="block h-full w-full cursor-zoom-in focus:outline-none focus:ring-[3px] focus:ring-accent-soft"
                  >
                    <img
                      src={estado.url}
                      alt={foto.nomeArquivo ?? 'Foto da conferência'}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ) : estado?.status === 'erro' ? (
                  <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-danger" />
                    <span className="text-[10px] text-tx-3">Falha ao carregar</span>
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <ArrowPathIcon className="h-5 w-5 animate-spin text-tx-3" />
                  </div>
                )}

                {mostrarRemover && (
                  <button
                    type="button"
                    onClick={() => handleRemover(foto)}
                    disabled={removerFotoMut.isPending}
                    className="absolute right-1.5 top-1.5 flex h-11 w-11 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-danger disabled:opacity-50 sm:h-9 sm:w-9"
                    aria-label="Remover foto"
                    title="Remover foto"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {mostrarUpload && (
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {/* Enviar arquivo: SEM `capture` — abre galeria (celular) / seletor (desktop). */}
          <label
            className={`${btnSecundario} w-full sm:w-auto ${
              enviarFotoMut.isPending ? 'pointer-events-none opacity-50' : 'cursor-pointer'
            }`}
          >
            <PhotoIcon className="h-4 w-4" />
            Enviar arquivo
            <input
              type="file"
              accept="image/*"
              disabled={enviarFotoMut.isPending}
              onChange={handleArquivoSelecionado}
              className="hidden"
            />
          </label>

          {/* Tirar foto: `capture="environment"` — abre a câmera do sistema no celular.
              No desktop cai no seletor de arquivo (sem câmera), e o botão de cima cobre esse caso. */}
          <label
            className={`${btnPrimario} w-full sm:w-auto ${
              enviarFotoMut.isPending ? 'pointer-events-none opacity-50' : 'cursor-pointer'
            }`}
          >
            {enviarFotoMut.isPending ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <CameraIcon className="h-4 w-4" />
                Tirar foto
              </>
            )}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              disabled={enviarFotoMut.isPending}
              onChange={handleArquivoSelecionado}
              className="hidden"
            />
          </label>
        </div>
      )}

      {/* Lightbox: foto em resolução original (object-contain, quase tela cheia). Fecha no ×,
          ESC (capture, ver efeito acima) ou clique fora da imagem. */}
      {ampliada && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Foto ${ampliada.nome ?? 'da conferência'}`}
          onClick={() => setAmpliada(null)}
        >
          <div className="absolute inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" />
          <button
            type="button"
            onClick={() => setAmpliada(null)}
            aria-label="Fechar"
            className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
          <img
            src={ampliada.url}
            alt={ampliada.nome ?? 'Foto da conferência'}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] max-w-[96vw] cursor-default rounded-iris object-contain shadow-iris-xl"
          />
        </div>
      )}
    </div>
  );
};

export default GaleriaFotosConferencia;
